import { useState } from 'react';
import PropTypes from 'prop-types';
import { Select, MenuItem, Checkbox, ListItemText, FormControl, InputLabel, OutlinedInput, Button, TextField, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Reusable MultiSelect Component with Checkboxes and Search
 * Displays selected values as comma-separated text, with checkbox dropdown
 *
 * @param {String} label - Select label
 * @param {Array} value - Selected values (array of IDs or values)
 * @param {Function} onChange - Change handler
 * @param {Array} options - Array of options {id, name} or {value, label}
 * @param {Boolean} required - Whether field is required
 * @param {Boolean} disabled - Whether select is disabled
 * @param {Boolean} fullWidth - Whether to take full width
 * @param {Object} sx - Additional Material UI sx styles
 * @param {String} placeholder - Placeholder text
 * @param {String} emptyMessage - Message when no options available
 */
function MultiSelect({
    label,
    value = [],
    onChange,
    options = [],
    required = false,
    disabled = false,
    fullWidth = true,
    sx = {},
    placeholder = '',
    emptyMessage,
    id,
    name,
    'data-testid': dataTestId,
    ...otherProps
}) {
    const { t } = useTranslation();
    const localizedEmptyMessage = emptyMessage ?? t('table.noData');
    const [searchText, setSearchText] = useState('');

    // Normalize value array to ensure consistent type comparison (handle both number and string IDs)
    const normalizedValue = Array.isArray(value) 
        ? value.map(v => v != null ? String(v) : null).filter(v => v !== null)
        : [];

    // Filter options based on search text
    const filteredOptions = options.filter((option) => {
        const optionLabel = option.name ?? option.label ?? '';
        return optionLabel.toLowerCase().includes(searchText.toLowerCase());
    });

    // Handle change event
    const handleChange = (event) => {
        onChange(event);
    };

    // Check if all options are selected
    const allSelected = options.length > 0 && normalizedValue.length === options.length;

    // Handle select all / unselect all toggle
    const handleSelectAll = (event) => {
        event.stopPropagation();
        if (allSelected) {
            // Unselect all
            onChange({
                target: {
                    name: name,
                    value: [],
                },
            });
        } else {
            // Select all
            const allValues = options.map((option) => option.id ?? option.value);
            onChange({
                target: {
                    name: name,
                    value: allValues,
                },
            });
        }
    };

    // Get selected names for display
    const selectedNames = options
        .filter((option) => {
            const optionValue = option.id ?? option.value;
            if (optionValue == null) return false;
            // Check if value includes this option (handle both number and string)
            return Array.isArray(value) && value.some(v => 
                v === optionValue || 
                String(v) === String(optionValue) ||
                Number(v) === Number(optionValue)
            );
        })
        .map((option) => option.name ?? option.label)
        .join(', ');

    return (
        <FormControl
            fullWidth={fullWidth}
            size="small"
            sx={{ width: '100%', minWidth: 0, '& .MuiInputBase-root': { height: 'auto', minHeight: '40px' }, ...sx }}
        >
            <InputLabel shrink id={`${id}-label`}>
                {label}
            </InputLabel>
            <Select
                labelId={`${id}-label`}
                id={id}
                name={name}
                multiple
                value={Array.isArray(value) ? value : []}
                onChange={handleChange}
                input={<OutlinedInput notched label={label} />}
                renderValue={() => selectedNames || placeholder}
                disabled={disabled}
                required={required}
                displayEmpty
                inputProps={{
                    'data-testid': dataTestId,
                }}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 300,
                        },
                    },
                }}
                sx={{
                    width: '100%',
                    '& .MuiSelect-select': {
                        color: value.length === 0 ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                        overflow: 'hidden !important',
                        textOverflow: 'ellipsis !important',
                        whiteSpace: 'nowrap !important',
                        display: 'block !important',
                        paddingRight: '32px !important',
                        textAlign: 'left !important',
                    },
                    '& .MuiInputBase-input': {
                        overflow: 'hidden !important',
                        textOverflow: 'ellipsis !important',
                        whiteSpace: 'nowrap !important',
                        display: 'block !important',
                    },
                    '& .MuiOutlinedInput-input': {
                        overflow: 'hidden !important',
                        textOverflow: 'ellipsis !important',
                        whiteSpace: 'nowrap !important',
                        display: 'block !important',
                    },
                }}
                {...otherProps}
            >
                <Box sx={{ p: 1, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                    <Button size="small" onClick={handleSelectAll} sx={{ mb: 1, textTransform: 'none' }}>
                        {allSelected ? 'UNSELECT ALL' : 'SELECT ALL'}
                    </Button>
                    <TextField
                        size="small"
                        placeholder="Search"
                        fullWidth
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </Box>
                {filteredOptions.length === 0 ? (
                    <MenuItem disabled>{localizedEmptyMessage}</MenuItem>
                ) : (
                    filteredOptions.map((option, index) => {
                        const optionValue = option.id ?? option.value;
                        const optionLabel = option.name ?? option.label;
                        // Check if this option is selected (handle both number and string)
                        const isSelected = Array.isArray(value) && optionValue != null && value.some(v => 
                            v === optionValue || 
                            String(v) === String(optionValue) ||
                            Number(v) === Number(optionValue)
                        );

                        return (
                            <MenuItem key={`option-${index}-${optionValue}`} value={optionValue}>
                                <Checkbox checked={isSelected} />
                                <ListItemText primary={optionLabel} />
                            </MenuItem>
                        );
                    })
                )}
            </Select>
        </FormControl>
    );
}

MultiSelect.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
        })
    ),
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    fullWidth: PropTypes.bool,
    sx: PropTypes.object,
    placeholder: PropTypes.string,
    emptyMessage: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    'data-testid': PropTypes.string,
};

export default MultiSelect;
